import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    IconButton,
    Tooltip,
    Typography,
    Pagination,
} from '@mui/material';
import { Visibility, Add } from '@mui/icons-material';
import { StocktakeService } from '../../services/stocktake.service';
import {
    StocktakeDTO,
    StocktakeStatus,
    STOCKTAKE_STATUS_LABELS,
} from '../../interfaces/stocktake.interface';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd',
};

const STATUS_OPTIONS: StocktakeStatus[] = ['DRAFT', 'PENDING_APPROVAL', 'POSTED', 'CANCELLED'];

const StocktakeList: React.FC = () => {
    const navigate = useNavigate();
    const [stocktakes, setStocktakes] = useState<StocktakeDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0,
    });

    const handleViewDetails = (id: string) => {
        navigate(`/stocktakes/${id}`);
    };

    const fetchStocktakes = async (page: number = 0, status: string = statusFilter) => {
        try {
            setLoading(true);
            const response = await StocktakeService.getStocktakesByInstitution(status, page);
            setStocktakes(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements,
            });
            setError('');
        } catch (err) {
            setError('Failed to load stocktakes');
            console.error('Error fetching stocktakes:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStocktakes(0, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handleStatusChange = (event: SelectChangeEvent) => {
        setStatusFilter(event.target.value);
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchStocktakes(page - 1, statusFilter);
    };

    return (
        <div style={{ padding: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Stocktakes</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/stocktakes/create')}
                >
                    New Stocktake
                </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={handleStatusChange}>
                        <MenuItem value="">All</MenuItem>
                        {STATUS_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>
                                {STOCKTAKE_STATUS_LABELS[status]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    Total: {pageInfo.totalElements}
                </Typography>
            </Box>

            {loading ? (
                <div data-testid="loading-state">Loading...</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }} data-testid="stocktakes-table">
                    <thead>
                        <tr>
                            <th style={tableHeaderStyle}>Reference No</th>
                            <th style={tableHeaderStyle}>Store</th>
                            <th style={tableHeaderStyle}>Date</th>
                            <th style={tableHeaderStyle}>Status</th>
                            <th style={tableHeaderStyle}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stocktakes.length === 0 ? (
                            <tr>
                                <td style={tableCellStyle} colSpan={5} align="center">
                                    No stocktakes found
                                </td>
                            </tr>
                        ) : (
                            stocktakes.map((stocktake) => (
                                <tr key={stocktake.id} data-testid={`stocktake-row-${stocktake.id}`}>
                                    <td style={tableCellStyle}>{stocktake.referenceNo}</td>
                                    <td style={tableCellStyle}>{stocktake.storeName || '-'}</td>
                                    <td style={tableCellStyle}>
                                        {stocktake.stocktakeDate ? formatDate(stocktake.stocktakeDate) : '-'}
                                    </td>
                                    <td style={tableCellStyle}>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-block',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                bgcolor: 'warning.light',
                                                color: 'warning.dark',
                                            }}
                                        >
                                            {STOCKTAKE_STATUS_LABELS[stocktake.status] ?? stocktake.status}
                                        </Box>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <Tooltip title="View Details" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewDetails(stocktake.id)}
                                                color="primary"
                                                data-testid={`view-button-${stocktake.id}`}
                                                aria-label="View Details"
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {error && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                    {error}
                </Box>
            )}
        </div>
    );
};

export default StocktakeList;
